import { Component, computed, ElementRef, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromChats from '../../../..';
import * as fromGenerated from '../../../../../_generated';
import * as fromFeatureFlags from '../../../../../feature-flags';
import { MessageQuickActionsCopyComponent } from './message-quick-actions-copy.component';
import { MessageQuickActionsExportComponent } from './message-quick-actions-export.component';

@Component({
  selector: 'squadbox-message-quick-actions-smart',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseAiIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    TranslateModule,
    MessageQuickActionsCopyComponent,
    MessageQuickActionsExportComponent,
  ],
  template: `
    @if (ffQuickActions().copy) {
      <squadbox-message-quick-actions-copy
        [messageContent]="messageContent()"
        [chatId]="chatId()"
        [messageId]="messageId()"
      />
    }
    @if (ffQuickActions().export.hasAnyTrueValue) {
      <squadbox-message-quick-actions-export
        [chatUuid]="chatUuid()"
        [chatId]="chatId()"
        [messageId]="messageId()"
      />
    }
    @if (ffQuickActions().thumbsUp) {
      <button sdIconButton variant="icon" color="neutral" size="xs">
        <svg sdUseUIBasicIcon="thumbs-up"></svg>
      </button>
    }
    @if (ffQuickActions().thumbsDown) {
      <button sdIconButton variant="icon" color="neutral" size="xs">
        <svg sdUseUIBasicIcon="thumbs-down"></svg>
      </button>
    }
    @if (
      ffQuickActions().starsAi.hasAnyTrueValue &&
      !isProcessing() &&
      isLastResponse()
    ) {
      <button
        sdIconButton
        variant="icon"
        color="primary"
        size="xs"
        [sdDropdownMenuTrigger]="starsAiDropdown"
      >
        <svg sdUseAiIcon="stars"></svg>
      </button>

      <sd-dropdown-menu
        #starsAiDropdown
        [options]="starsAiDropdownMenuOptions()"
      ></sd-dropdown-menu>
    }
  `,
  styles: [
    `
      @reference '../../../../../../styles.css';

      :host {
        @apply flex gap-1;
      }
    `,
  ],
})
export class MessageQuickActionsSmartComponent {
  private readonly featureFlagsStore =
    inject<Store<fromFeatureFlags.FeatureFlagsState>>(Store);
  private readonly chatsStore = inject<Store<fromChats.ChatsState>>(Store);
  private readonly translate = inject(TranslateService);
  public readonly elementRef = inject(ElementRef);
  public readonly chatUuid = input.required<string>();
  public readonly chatId = input.required<number>();
  public readonly messageId = input.required<number>();
  public readonly messageContent = input.required<string>();
  public readonly isProcessing = input.required<boolean>();
  public readonly isLastResponse = input<boolean>(false);
  public readonly ffQuickActions = this.featureFlagsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectQuickActions,
  );

  public readonly starsAiDropdownMenuOptions = computed<
    fromSeriousSystem.DropdownMenuOption[]
  >(() => [
    {
      label: this.translate.instant(
        'CHAT.DROPDOWN_MENU.IMPROVE_WRITING',
      ) as string,
      icon: { type: 'uiBasic', icon: 'edit' },
      options: [
        {
          label: this.translate.instant(
            'CHAT.DROPDOWN_MENU.SIMPLIFY_LANGUAGE',
          ) as string,
          action: () => this.onTriggerStarsAiAction('QUICK_ACTION_SIMPLIFY'),
        },
        {
          label: this.translate.instant(
            'CHAT.DROPDOWN_MENU.ENHANCE_VOCABULARY',
          ) as string,
          action: () => this.onTriggerStarsAiAction('QUICK_ACTION_ENHANCE'),
        },
      ],
    },
    {
      label: this.translate.instant(
        'CHAT.DROPDOWN_MENU.MAKE_SHORTER',
      ) as string,
      action: () => this.onTriggerStarsAiAction('QUICK_ACTION_SHORTER'),
      icon: { type: 'uiBasic', icon: 'text-short' },
    },
    {
      label: this.translate.instant('CHAT.DROPDOWN_MENU.TRANSLATE') as string,
      icon: { type: 'uiBasic', icon: 'translation' },
      options: [
        {
          label: this.translate.instant('CHAT.DROPDOWN_MENU.ENGLISH') as string,
          action: () =>
            this.onTriggerStarsAiAction('QUICK_ACTION_TRANSLATE_TO_ENGLISH'),
        },
        {
          label: this.translate.instant('CHAT.DROPDOWN_MENU.FRENCH') as string,
          action: () =>
            this.onTriggerStarsAiAction('QUICK_ACTION_TRANSLATE_TO_FRENCH'),
        },
        {
          label: this.translate.instant('CHAT.DROPDOWN_MENU.SPANISH') as string,
          action: () =>
            this.onTriggerStarsAiAction('QUICK_ACTION_TRANSLATE_TO_SPANISH'),
        },
      ],
    },
    {
      label: this.translate.instant('CHAT.DROPDOWN_MENU.CHANGE_TONE') as string,
      icon: { type: 'uiBasic', icon: 'voice' },
      options: [
        {
          label: this.translate.instant(
            'CHAT.DROPDOWN_MENU.PROFESSIONAL',
          ) as string,
          action: () =>
            this.onTriggerStarsAiAction('QUICK_ACTION_TONE_PROFESSIONAL'),
        },
        {
          label: this.translate.instant('CHAT.DROPDOWN_MENU.CASUAL') as string,
          action: () => this.onTriggerStarsAiAction('QUICK_ACTION_TONE_CASUAL'),
        },
        {
          label: this.translate.instant(
            'CHAT.DROPDOWN_MENU.STRAIGHTFORWARD',
          ) as string,
          action: () =>
            this.onTriggerStarsAiAction('QUICK_ACTION_TONE_STRAIGHTFORWARD'),
        },
      ],
    },
  ]);

  public onTriggerStarsAiAction(
    action: fromGenerated.RequestQuickActionDto.QuickActionEnum,
  ) {
    const chatUuid = this.chatUuid();

    if (chatUuid) {
      this.chatsStore.dispatch(
        fromChats.ChatWithMessagesActions.requestQuickAction({
          chatUuid,
          chatMessageId: this.messageId(),
          quickAction: action,
          quickActionMessage: this.translate.instant(
            `CHAT.QUICK_ACTIONS_MESSAGE.${action}`,
          ) as string,
        }),
      );
    }
  }
}
